import React, { useState } from 'react';
import { supabase } from './supabase';
import { t } from './translations';

// ─── PROMPTED GLOWS ──────────────────────────────────────────────────────────
// Jar owner sets a custom question. Senders see it as a prompt above the text field.
// Stored in profiles.active_prompt (text, nullable)

const SUGGESTED_PROMPTS = {
  en: [
    "What's one thing you've never told me but always wanted to?",
    "What do you love most about who I'm becoming?",
    "What's a moment I made you smile that you still think about?",
    "What's one word you'd use to describe me — and why?",
    "What do you think I don't hear enough?",
    "If you could give me one piece of advice for life, what would it be?",
    "What's something you admire in me that I probably don't see in myself?",
    "What's the kindest thing I've done for you?",
  ],
  ar: [
    "إيه حاجة ما قلتهاليش أبداً بس عايز تقولها؟",
    "إيه اللي بتحبه أكتر في اللي بتبقاه؟",
    "فين لحظة خليتك تبتسم وبتفكر فيها لسه؟",
    "لو هتوصفني بكلمة واحدة، هتقول إيه وليه؟",
    "إيه اللي بتفتكر إني محتاج أسمعه أكتر؟",
    "لو هتديني نصيحة واحدة في الحياة، هتقول إيه؟",
    "إيه حاجة بتعجبك فيّا وممكن أنا نفسي ما بشوفهاش؟",
    "إيه أحسن حاجة عملتها معاك؟",
  ],
};

// ── Prompt Settings Panel (inside Profile Edit) ──────────────────────────────
export function PromptSettings({ profile, lang, onSaved }) {
  const T = t[lang];
  const [prompt, setPrompt] = useState(profile?.active_prompt || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  async function save() {
    setSaving(true);
    await supabase.from('profiles')
      .update({ active_prompt: prompt.trim() || null })
      .eq('id', profile.id);
    setSaving(false); setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    if (onSaved) onSaved(prompt.trim() || null);
  }

  async function clear() {
    setPrompt('');
    setSaving(true);
    await supabase.from('profiles').update({ active_prompt: null }).eq('id', profile.id);
    setSaving(false);
    if (onSaved) onSaved(null);
  }

  return (
    <div style={{background:'rgba(255,209,102,0.05)',border:'1px solid var(--border)',borderRadius:'var(--radius-sm)',padding:18,marginBottom:20}}>
      <div style={{fontWeight:600,fontSize:'0.88rem',marginBottom:4,color:'var(--gold)'}}>
        💬 {lang==='ar'?'سؤال الجرّة (اختياري)':'Jar prompt (optional)'}
      </div>
      <p style={{fontSize:'0.78rem',color:'var(--text-muted)',marginBottom:12,lineHeight:1.6}}>
        {lang==='ar'
          ?'اسأل الناس سؤال معين عشان يجاوبوا عليه بدل ما يكتبوا بشكل عام'
          :"Ask senders a specific question — they'll answer it instead of writing freely"}
      </p>

      <textarea
        value={prompt}
        onChange={e=>setPrompt(e.target.value)}
        placeholder={lang==='ar'?'مثلاً: إيه حاجة بتحبها فيّا؟':'e.g. What do you love most about me?'}
        maxLength={200}
        style={{minHeight:72,marginBottom:10,textAlign:T.dir==='rtl'?'right':'left',resize:'none'}}
      />

      {/* Suggested prompts */}
      <div style={{marginBottom:12}}>
        <div style={{fontSize:'0.72rem',color:'var(--text-muted)',marginBottom:8}}>
          {lang==='ar'?'اقتراحات:':'Suggestions:'}
        </div>
        <div style={{display:'flex',flexWrap:'wrap',gap:6}}>
          {SUGGESTED_PROMPTS[lang].slice(0,4).map((p,i)=>(
            <button key={i} onClick={()=>setPrompt(p)}
              style={{fontSize:'0.72rem',padding:'4px 10px',borderRadius:100,background:'var(--bg3)',border:'1px solid var(--border)',color:'var(--text-muted)',cursor:'pointer',fontFamily:'inherit',textAlign:'left',lineHeight:1.4,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
              {p}
            </button>
          ))}
        </div>
      </div>

      <div style={{display:'flex',gap:8}}>
        <button className="btn btn-glow" style={{fontSize:'0.82rem',padding:'9px 18px'}} onClick={save} disabled={saving}>
          {saving?'…':saved?(lang==='ar'?'✓ اتحفظ':'✓ Saved'):(lang==='ar'?'حفظ السؤال':'Save prompt')}
        </button>
        {prompt && (
          <button className="btn btn-ghost" style={{fontSize:'0.82rem',padding:'9px 18px'}} onClick={clear}>
            {lang==='ar'?'مسح':'Clear'}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Active Prompt Banner (shown on Send page) ─────────────────────────────────
export function PromptBanner({ prompt, lang }) {
  if (!prompt) return null;
  const T = t[lang];
  return (
    <div style={{background:'linear-gradient(135deg,rgba(255,209,102,0.1),rgba(45,212,191,0.07))',border:'1px solid rgba(255,209,102,0.25)',borderRadius:14,padding:'14px 18px',marginBottom:16,display:'flex',gap:12,alignItems:'flex-start'}}>
      <span style={{fontSize:'1.2rem',flexShrink:0}}>💬</span>
      <div>
        <div style={{fontSize:'0.72rem',color:'var(--gold)',fontWeight:600,letterSpacing:'0.06em',textTransform:'uppercase',marginBottom:4}}>
          {lang==='ar'?'سؤال الجرّة':'Jar prompt'}
        </div>
        <div style={{fontSize:'0.9rem',color:'var(--text)',lineHeight:1.65,fontStyle:'italic'}}>
          "{prompt}"
        </div>
      </div>
    </div>
  );
}
